import React, { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { EMAIL, PHONE, PHONE_RAW, WHATSAPP_BASE, services } from "../mock";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", age: "", service: "", message: "" });
  const { toast } = useToast();

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast({ title: "Please fill required fields", description: "Name, phone & message are required." });
      return;
    }
    const text = `Hi! I'm ${form.name} (${form.phone}).%0A` +
      (form.email ? `Email: ${form.email}%0A` : "") +
      (form.age ? `Child's age: ${form.age}%0A` : "") +
      (form.service ? `Service: ${form.service}%0A` : "") +
      `%0AMessage: ${form.message}`;
    const url = `${WHATSAPP_BASE}?text=${text}`;
    window.open(url, "_blank");
    toast({ title: "Opening WhatsApp…", description: "Your enquiry is ready to send." });
  };

  return (
    <section id="contact" className="py-16 lg:py-24 bg-gradient-to-b from-transparent to-orange-50/50">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 items-start">
        <div className="reveal-left">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 text-[#ee6c4d] text-xs font-semibold tracking-[0.2em] uppercase">
            Get in touch
          </div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl text-[#122b45] leading-tight">
            Let&apos;s take that first <span className="text-[#ee6c4d]">step together.</span>
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-lg text-sm sm:text-base">
            Share a few details about your child and tap send — your enquiry will open in WhatsApp, so we can chat with you right away.
          </p>

          <div className="mt-8 space-y-4">
            <a href={`tel:${PHONE_RAW}`} className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-orange-100 hover:border-[#ee6c4d] transition">
              <div className="w-11 h-11 rounded-xl bg-orange-100 text-[#ee6c4d] flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider">Call us</div>
                <div className="font-semibold text-[#122b45]">{PHONE}</div>
              </div>
            </a>
            <a href={`mailto:${EMAIL}`} className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-orange-100 hover:border-[#ee6c4d] transition">
              <div className="w-11 h-11 rounded-xl bg-green-100 text-green-600 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider">Email</div>
                <div className="font-semibold text-[#122b45] break-all">{EMAIL}</div>
              </div>
            </a>
            <div className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-orange-100">
              <div className="w-11 h-11 rounded-xl bg-orange-100 text-[#ee6c4d] flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider">Visit</div>
                <div className="font-semibold text-[#122b45]">Patna, Bihar, India</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 sm:p-7 md:p-9 border border-orange-100 shadow-[0_30px_60px_-30px_rgba(18,43,69,0.25)] space-y-4 reveal-right reveal-delay-100">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#122b45]">Parent&apos;s Name *</label>
              <Input value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Your full name" className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#122b45]">Phone *</label>
              <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+91" className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#122b45]">Email</label>
              <Input value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="name@email.com" type="email" className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#122b45]">Child&apos;s Age</label>
              <Input value={form.age} onChange={(e) => upd("age", e.target.value)} placeholder="e.g., 4 years" className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#122b45]">Service of interest</label>
            <Select value={form.service} onValueChange={(v) => upd("service", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((s) => (
                  <SelectItem key={s.title} value={s.title}>{s.title}</SelectItem>
                ))}
                <SelectItem value="Not sure">Not sure — please advise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#122b45]">Message *</label>
            <Textarea value={form.message} onChange={(e) => upd("message", e.target.value)} placeholder="Tell us briefly about your child…" rows={4} className="mt-1.5 rounded-xl resize-none" />
          </div>

          <Button type="submit" className="w-full h-12 rounded-full bg-[#25d366] hover:bg-[#20bd5a] text-white font-semibold">
            <Send className="w-4 h-4 mr-2" /> Send via WhatsApp
          </Button>
          <a
            href={`${WHATSAPP_BASE}?text=Hi!%20I'd%20like%20to%20enquire%20about%20therapy%20services%20at%20Nanhe%20Kadam.`}
            target="_blank"
            rel="noreferrer"
            className="block text-center text-sm text-[#122b45] hover:text-[#ee6c4d] transition"
          >
            <MessageCircle className="inline w-4 h-4 mr-1" /> Or chat directly on WhatsApp
          </a>
        </form>
      </div>
    </section>
  );
}
